import { useEffect, useRef } from 'react';
import { DEFAULT_ERROR, isFetchBaseQueryError, isServerError } from '../utils/server-error';
import { useAppDispatch } from '../store';
import { toastActions } from '../slices/toast.slice';
import { useTranslation } from 'react-i18next';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import { SerializedError } from '@reduxjs/toolkit';
import { ErrorCode } from '../../i18n';

interface ErrorHandlingOptions {
    inlineErrors: Array<ErrorCode> | boolean;
    onInlineError: (error: InlineError) => void;
}

export type InlineError = {
    errorCode: ErrorCode; // TODO this could be typed better eventually
    message: string;
};

function isInlineError(inlineErrors: unknown, errorCode): errorCode is ErrorCode {
    return Array.isArray(inlineErrors) ? inlineErrors.includes(errorCode) : !!inlineErrors;
}

export function useErrorHandling(
    error: FetchBaseQueryError | SerializedError | undefined,
    options?: ErrorHandlingOptions
) {
    const optionsRef = useRef(options);

    const { t } = useTranslation('errors');

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!error) {
            return;
        }

        const { inlineErrors, onInlineError } = optionsRef.current || {};

        const message =
            isFetchBaseQueryError(error) && isServerError(error.data) ? t(error.data.errorCode) : DEFAULT_ERROR;

        if (
            isFetchBaseQueryError(error) &&
            isServerError(error.data) &&
            isInlineError(inlineErrors, error.data.errorCode) &&
            onInlineError
        ) {
            onInlineError({
                errorCode: error.data.errorCode,
                message
            });
        } else {
            dispatch(
                toastActions.showMessage({
                    data: {
                        type: 'error',
                        text: message
                    }
                })
            );
        }
    }, [t, dispatch, error]);
}
